import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Card,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Collapse,
  CircularProgress,
  makeStyles,
  Divider
} from '@material-ui/core'
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  Alert
} from '@material-ui/lab'
import {
  ExpandMore as ExpandMoreIcon
} from '@material-ui/icons'
import { useTrackingService } from './TrackingService'
import CngBackdrop from '../../../components/cngcomponents/CngBackDrop'
import {
  components,
  constants
} from 'cng-web-lib'
import DexpTrackingTranslationText from '../contexts/DexpTrackingTranslationText'


const {
  button: { CngButton },
  table: { useFetchCodeMaintenanceLookup }
} = components

const { filter: { EQUAL } } = constants

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: theme.spacing(3),
    backgroundColor: '#f8f9fa',
    minHeight: '100vh'
  },
  searchContainer: {
    backgroundColor: 'white',
    padding: theme.spacing(3),
    borderRadius: 8,
    marginBottom: theme.spacing(3),
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e0e0e0'
  },
  searchForm: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch'
    }
  },
  statusCard: {
    backgroundColor: 'white',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: 8,
    borderLeft: '6px solid #28a745',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    position: 'relative',
    overflow: 'hidden'
  },
  timelineCard: {
    backgroundColor: 'white',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  statusHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
    flexWrap: 'wrap'
  },
  statusChip: {
    backgroundColor: 'transparent',
    color: '#4caf50',
    fontWeight: 700,
    fontSize: '1.4rem',
    padding: '8px 0px',
    height: 'auto',
    borderRadius: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    display: 'flex',
    alignItems: 'center',
    '&:before': {
      content: '""',
      width: 18,
      height: 18,
      borderRadius: '50%',
      // Use CSS variable so we can change dot color per state
      backgroundColor: 'var(--statusDotColor, #4caf50)',
      marginRight: theme.spacing(1.5),
      flexShrink: 0
    }
  },
  daysContainer: {
    position: 'absolute',
    right: theme.spacing(3),
    top: '50%',
    transform: 'translateY(-50%)',
    textAlign: 'right',
    [theme.breakpoints.down('sm')]: {
      position: 'static',
      transform: 'none',
      marginTop: theme.spacing(2),
      textAlign: 'right'
    }
  },
  daysNumber: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#000',
    lineHeight: 1
  },
  daysLabel: {
    fontSize: '0.875rem',
    color: '#666',
    fontWeight: 500
  },
  containerInfo: {
    color: '#555',
    fontSize: '1rem',
    fontWeight: 500,
    marginBottom: theme.spacing(1)
  },
  timelineContainer: {
    padding: 0,
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(2)
  },
  timelineItem: {
    '&:before': {
      display: 'none'
    },
    minHeight: 90,
    alignItems: 'flex-start',
    display: 'flex',
    marginBottom: theme.spacing(2)
  },
  timelineDot: {
    margin: 0,
    padding: 0,
    boxShadow: 'none',
    width: 16,
    height: 16,
    fontSize: '0.875rem',
    position: 'relative',
    zIndex: 2,
    marginTop: theme.spacing(1) // Lower the dot to align with event content
  },
  supersededEventTitle: {
    fontWeight: 700,
    fontSize: '1.1rem',
    marginBottom: theme.spacing(0.5),
    color: '#999',
    lineHeight: 1.2
  },
  supersededEventLocation: {
    color: '#999',
    fontSize: '0.95rem',
    lineHeight: 1.4
  },
  supersededDateText: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: '#999',
    textAlign: 'left',
    whiteSpace: 'nowrap'
  },
  supersededTimeText: {
    fontSize: '0.875rem',
    color: '#999',
    marginTop: 2,
    textAlign: 'left',
    whiteSpace: 'nowrap'
  },
  timelineContent: {
    paddingLeft: theme.spacing(3),
    paddingRight: 0,
    paddingBottom: 0,
    flex: 1
  },
  eventSection: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e0e0e0'
  },
  eventSectionSuperseded: {
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    border: '1px solid #e0e0e0'
  },
  eventContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
    cursor: 'pointer',
    borderRadius: 4,
    padding: theme.spacing(1),
    margin: theme.spacing(-1),
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)'
    }
  },
  eventContainerNonClickable: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
    padding: theme.spacing(1),
    margin: theme.spacing(-1)
  },
  eventTimeColumn: {
    minWidth: 80,
    flexShrink: 0,
    marginRight: theme.spacing(2)
  },
  eventDetails: {
    flex: 1
  },
  eventTitle: {
    fontWeight: 700,
    fontSize: '1.1rem',
    marginBottom: theme.spacing(0.5),
    color: '#333',
    lineHeight: 1.2
  },
  eventLocation: {
    color: '#666',
    fontSize: '0.95rem',
    lineHeight: 1.4,
    whiteSpace: 'pre-line'
  },
  eventTime: {
    minWidth: 100,
    flexShrink: 0,
    marginRight: theme.spacing(2)
  },
  dateText: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: '#333',
    textAlign: 'left',
    whiteSpace: 'nowrap'
  },
  timeText: {
    fontSize: '0.875rem',
    color: '#666',
    marginTop: 2,
    textAlign: 'left',
    whiteSpace: 'nowrap'
  },
  expandButton: {
    marginLeft: theme.spacing(1),
    flexShrink: 0
  },
  expandedContent: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: '1px dotted #d0d0d0',
    pointerEvents: 'none'
  },
  subEventItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: theme.spacing(1, 0),
    minHeight: 28
  },
  subEventTitle: {
    fontSize: '0.875rem',
    color: '#666',
    fontWeight: 400,
    flex: 1,
    lineHeight: 1.3
  },
  subEventTitleHighlightBold: {
    fontSize: '0.875rem',
    color: '#333',
    fontWeight: 700,
    flex: 1,
    lineHeight: 1.3,
    display: 'flex',
    alignItems: 'center',
    '& .highlight-dot': {
      width: 6,
      height: 6,
      borderRadius: '50%',
      backgroundColor: '#4caf50',
      marginRight: theme.spacing(1),
      flexShrink: 0
    }
  },
  subEventTime: {
    fontSize: '0.8rem',
    color: '#666',
    fontWeight: 400,
    minWidth: 120,
    flexShrink: 0,
    textAlign: 'right'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200
  },
  bottomSpace: {
    marginBottom: theme.spacing(3)
  },
  titleText: {
    fontSize: '1.5rem',
    marginBottom: 10,
    textAlign: 'left',    
  },
  loginButton: {
    backgroundColor: '#f3a73e !important',
    font: '#ffffff !important',
  },
  registerButton: {
    backgroundColor: '#000000 !important',
    color: '#ffffff !important',
  },
  // Compliance Overview Styles
  complianceHeader: {
    padding: '12px 23px 5px',
    backgroundColor: props => (props.containsRejected ? '#fff8f1' : '#f9f9f9'),
    borderBottom: props => (props.containsRejected ? '1px solid #fd7e14' : '1px solid transparent'),
    '& .MuiTypography-root': {
      fontWeight: 'bold',
      fontSize: 'large',
    },
  },
  complianceContent: {
    padding: '15px 22px 0px',
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: theme.spacing(2)
    }
  },
  complianceSection: {
    flex: 1,
    '&.master-level': {
      marginRight: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        marginRight: 0
      }
    },
    '&.house-level': {
      marginLeft: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        marginLeft: 0
      }
    }
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1)
  },
  sectionDescription: {
    marginBottom: theme.spacing(1)
  },
  statusContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: theme.spacing(1),
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  csStatusCard: {
    padding: '8px 12px',
    borderRadius: '4px',
    marginRight: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    height: '2em',
    border: 'none',
    minWidth: 'auto'
  },
  statusDot: {
    borderRadius: '50%',
    width: '10px',
    height: '10px',
    display: 'inline-block',
    marginRight: theme.spacing(1),
    flexShrink: 0
  },
  statusText: {
    fontWeight: '600'
  },
  // Status color variants
  acceptedStatus: {
    backgroundColor: '#e8f0fe',
    '& $statusText': {
      color: '#004b87'
    },
    '& $statusDot': {
      backgroundColor: '#004b87'
    }
  },
  rejectedStatus: {
    backgroundColor: '#fce8e6',
    '& $statusText': {
      color: '#dc3545'
    },
    '& $statusDot': {
      backgroundColor: '#dc3545'
    }
  },
  releasedStatus: {
    backgroundColor: '#e8f5e8',
    '& $statusText': {
      color: '#28a745'
    },
    '& $statusDot': {
      backgroundColor: '#28a745'
    }
  },
  heldStatus: {
    backgroundColor: '#fce8e6',
    '& $statusText': {
      color: '#dc3545'
    },
    '& $statusDot': {
      backgroundColor: '#dc3545'
    }
  },
  noDataStatus: {
    backgroundColor: '#e0e0e0',
    '& $statusText': {
      color: '#999'
    },
    '& $statusDot': {
      backgroundColor: '#9e9e9e'
    }
  },
  searchNote: {
    color: '#666',
    fontStyle: 'italic'
  },
  dividerVertical: {
    [theme.breakpoints.down('md')]: {
      display: 'none'
    }
  }
}))

const computeConnectorHeight = (isExpanded, subEventsLength = 0) => {
  if (!isExpanded) return '100px'
  // matches previous logic but centralized
  return `${80 + (subEventsLength * 45) + 40}px`
}

const TrackingTimelineItem = React.memo(function TrackingTimelineItem({
  classes,
  event,
  index,
  total,
  isExpanded,
  onToggle
}) {
  return (
    <TimelineItem className={classes.timelineItem}>
      <Box className={classes.eventTimeColumn}>
        <Typography className={event.status === 'superseded' ? classes.supersededDateText : classes.dateText}>
          {event.date}
        </Typography>
        {event.time && (
          <Typography className={event.status === 'superseded' ? classes.supersededTimeText : classes.timeText}>
            {event.time}
          </Typography>
        )}
      </Box>

      <TimelineSeparator>
        <TimelineDot
          className={classes.timelineDot}
          style={{
            backgroundColor: event.color || '#bdbdbd',
            color: 'white',
            border: '2px solid white',
            boxShadow: `0 0 0 2px ${event.color || '#bdbdbd'}`
          }}
        />
        {index < total - 1 && (
          <TimelineConnector
            style={{
              backgroundColor: '#e0e0e0',
              width: 1,
              marginLeft: 1,
              position: 'absolute',
              zIndex: 1,
              top: '16px',
              bottom: 0,
              height: computeConnectorHeight(isExpanded, event.subEvents?.length || 0)
            }}
          />
        )}
      </TimelineSeparator>

      <TimelineContent className={classes.timelineContent}>
        <Box className={event.status === 'superseded' ? classes.eventSectionSuperseded : classes.eventSection}>
          <Box
            className={event.expandable ? classes.eventContainer : classes.eventContainerNonClickable}
            onClick={event.expandable ? onToggle : undefined}
            role={event.expandable ? 'button' : undefined}
            tabIndex={event.expandable ? 0 : undefined}
            onKeyDown={event.expandable ? (e) => e.key === 'Enter' && onToggle?.() : undefined}
          >
            <Box className={classes.eventDetails}>
              <Typography className={event.status === 'superseded' ? classes.supersededEventTitle : classes.eventTitle}>
                {event.title}
              </Typography>
              <Typography className={event.status === 'superseded' ? classes.supersededEventLocation : classes.eventLocation}>
                {event.location}
              </Typography>
            </Box>

            {event.expandable && (
              <IconButton
                size="small"
                className={classes.expandButton}
                style={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  pointerEvents: 'none'
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            )}
          </Box>

          {event.expandable && (
            <Collapse in={isExpanded}>
              <Box className={classes.expandedContent}>
                {event.subEvents?.map((subEvent, subIndex) => (
                  <Box key={`${event.id}-sub-${subIndex}`} className={classes.subEventItem}>
                    <Typography
                      className={
                        (subEvent.bold || subEvent.highlight) ? classes.subEventTitleHighlightBold :
                          classes.subEventTitle
                      }
                    >
                      {subEvent.highlight && <span className="highlight-dot" />}
                      {subEvent.title}
                    </Typography>
                    <Typography className={classes.subEventTime}>
                      {subEvent.time}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Collapse>
          )}
        </Box>
      </TimelineContent>
    </TimelineItem>
  )
})

const TrackingPage = ({ showNotification }) => {
  const notify = showNotification || (() => { })

  const [trackingType, setTrackingType] = useState('CONTAINER')
  const [searchInput, setSearchInput] = useState('')
  const [trackingData, setTrackingData] = useState(null)
  const [complianceSummaryData, setComplianceSummaryData] = useState(null)
  const [expandedItems, setExpandedItems] = useState({})
  const [containsRejected, setContainsRejected] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [previousTrackingType, setPreviousTrackingType] = useState(trackingType);
  const trackingService = useTrackingService()
  const dexpTrackingTranslationText = DexpTrackingTranslationText()
  const [showLoginRegisterButton, setShowLoginRegisterButton] = useState(false)
  const [loginUrl, setLoginUrl] = useState("")
  const [registerUrl, setRegisterUrl] = useState("")

  const classes = useStyles({containsRejected})
  
  const fetchCodeMaintenanceLookup = useFetchCodeMaintenanceLookup()

  // Utility for comparing UI events using absolute time (ms since epoch)
  const compareUIEventTimes = (eventA, eventB, reverse = true) => {
    const a = typeof eventA?.sortTime === 'number' ? eventA.sortTime : 0
    const b = typeof eventB?.sortTime === 'number' ? eventB.sortTime : 0

    // reverse=true means latest first (default), reverse=false means oldest first
    return reverse ? (b - a) : (a - b)
  }

  useEffect(() => {
    Promise.all([
      fetchCodeMaintenanceLookup(
        constants.CodeMaintenanceType.CODE_MASTER,
        undefined,
        [{ field: 'codeType', operator: EQUAL, value: 'DEXP_CONFIG' }],
        undefined,
        'code'
      )
    ]).then(
      ([dexpConfig]) => {
        if (Object.keys(dexpConfig).length > 0) {
          console.log("DEXP config found")
          
          const localStorageStr = window.localStorage.getItem("userPreference")
          const userPreference = JSON.parse(localStorageStr)
          const loginId = userPreference?.tprUserDetail?.userProfile?.loginId
          
          if (loginId == dexpConfig.GUEST_USERNAME) {
            setShowLoginRegisterButton(true)    
            setLoginUrl(dexpConfig.LOGIN_URL)    
            setRegisterUrl(dexpConfig.REGISTER_URL)
          }
        }
      }
    )
  })

  const handleTrack = async () => {
    setPreviousTrackingType(trackingType);

    if (!searchInput.trim()) {
      notify('error', dexpTrackingTranslationText.pleaseEnterSearchNumber)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await trackingService.unifiedTracking(
        searchInput.trim(),
        trackingType
      )
      setTrackingData(result)
      setComplianceSummaryData(result?.complianceSummary)
      
      notify('success', dexpTrackingTranslationText.retrieveSuccessMessage)
    } catch (error) {
      setTrackingData(null)
      setComplianceSummaryData(null)
      setError(dexpTrackingTranslationText.failedToRetrieveErrorMessage)
      notify('error', dexpTrackingTranslationText.failedToTrackErrorMessage)
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleTrack()
    }
  }

  const toggleExpanded = (eventId) => {
    setExpandedItems(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }))
  }

  function handleLogin() {
    window.location.href = loginUrl
  }

  function handleRegister() {
    window.location.href = registerUrl
  }

  const getStatusColor = (trackingDataParam) => {
    const status = trackingDataParam?.status
    if (!status) return '#757575'

    // Empty result
    if (status.toUpperCase().includes('NO EVENTS FOUND') ||
      status.toUpperCase().includes('NO RECORD FOUND')) {
      return '#757575'
    }

    // Try to find matching event color from trackingData
    if (trackingDataParam?.events?.length > 0) {
      // Find the event that matches the status title
      const matchingEvent = trackingDataParam.events.find(event => event.title === status)
      if (matchingEvent?.color) {
        return matchingEvent.color
      }

      // Use the first event's color as fallback for header
      const firstEvent = trackingDataParam.events[0]
      if (firstEvent?.color) {
        return firstEvent.color
      }
    }

    // Default fallback
    return '#757575'
  }

  // Calculate Master Level statistics (ACIOCN events)
  const masterLevelStats = useMemo(() => {
    const masterLevel = complianceSummaryData?.master_level || {}
    const breakdown = Array.isArray(masterLevel?.status_breakdown) ? masterLevel.status_breakdown : []

    const stats = breakdown.reduce(
      (acc, item) => {
        const key = String(item?.internal_enum ?? '').toUpperCase()
        const count = Number(item?.count) || 0

        if (key.includes('ACCEPT')) acc.accepted = count
        else if (key.includes('SUBMIT')) acc.submitted = count
        else if (key.includes('REJECT')) acc.rejected = count

        return acc
      },
      { accepted: 0, submitted: 0, rejected: 0 }
    )

    return {
      total: Number(masterLevel?.total_primary_ccns) || 0,
      accepted: stats.accepted,
      submitted: stats.submitted,
      rejected: stats.rejected
    }
  }, [complianceSummaryData])

  // Calculate House Level statistics (EHBL and CUSTOMS_RELEASE events)
  const houseLevelStats = useMemo(() => {
    const houseLevel = complianceSummaryData?.house_level || {}
    const breakdown = Array.isArray(houseLevel?.status_breakdown) ? houseLevel.status_breakdown : []

    const stats = breakdown.reduce(
      (acc, item) => {
        const key = String(item?.internal_enum ?? '').toUpperCase()
        const count = Number(item?.count) || 0

        if (key.includes('RELEASE')) acc.released = count
        else if (key.includes('ACCEPT')) acc.accepted = count
        else if (key.includes('SUBMIT')) acc.submitted = count
        else if (key.includes('HOLD')) acc.held = count
        else if (key.includes('REJECT')) acc.rejected = count

        return acc
      },
      { released: 0, accepted: 0, submitted: 0, held: 0, rejected: 0 }
    )

    return {
      totalEhbls: Number(houseLevel?.total_ehbls) || 0,
      released: stats.released,
      accepted: stats.accepted,
      submitted: stats.submitted,
      held: stats.held,
      rejected: stats.rejected
    }
  }, [complianceSummaryData])

  useEffect(() => {
    if(masterLevelStats.rejected > 0 || houseLevelStats.rejected > 0 || houseLevelStats.held > 0){
      setContainsRejected(true)
    }else{
      setContainsRejected(false)
    }
  }, [masterLevelStats.rejected, houseLevelStats.rejected, houseLevelStats.held]);

  const sortedEvents = useMemo(() => {
    const events = trackingData?.events
    if (!Array.isArray(events) || events.length === 0) return []

    // Sort events: active/completed first (most recent), superseded at bottom
    return events.slice().sort((a, b) => {
      if (a.status === 'superseded' && b.status !== 'superseded') return 1
      if (b.status === 'superseded' && a.status !== 'superseded') return -1

      return compareUIEventTimes(a, b) // Latest first
    })
  }, [trackingData?.events])

  return (
    <Box className={classes.root}>
      {/* Search Container */}
      <Card className={classes.searchContainer}>
        <Typography
          className={classes.titleText}>
          {dexpTrackingTranslationText.title}
        </Typography>
        <Box className={classes.searchForm}>
          <FormControl variant="outlined" size="medium" style={{ minWidth: 180 }}>
            <Select
              value={trackingType}
              onChange={(e) => setTrackingType(e.target.value)}
              displayEmpty
            >
              <MenuItem value="CONTAINER">{dexpTrackingTranslationText.containerNo}</MenuItem>
              <MenuItem value="CCN">{dexpTrackingTranslationText.ccn}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            variant="outlined"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={`Enter Number`}
            size="medium"
            style={{ flex: 1, maxWidth: 400 }}
            disabled={isLoading}
          />

          <CngButton
            color='primary'
            onClick={handleTrack}
            disabled={isLoading || !searchInput.trim()}
            size="large"
          >
            {isLoading ? <CircularProgress size={20} color="inherit" /> : 'TRACK'}
          </CngButton>
        </Box>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert severity="error" className={classes.bottomSpace} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading Backdrop */}
      {isLoading && <CngBackdrop loading />}

      {/* Tracking Results */}
      {trackingData && !isLoading && (
        <>
          {/* No Record Found */}
          {trackingData.noEventsFound ? (
            <Alert severity="error" className={classes.bottomSpace}>
              {dexpTrackingTranslationText.noRecordFound}
            </Alert>
          ) : (
            <>
              {/* Container history message */}
              <Box mb={2}>
                {previousTrackingType === 'CONTAINER' && (
                  <Alert severity="info">
                    {dexpTrackingTranslationText.viewing} <b>{dexpTrackingTranslationText.containerHistory}</b>. {dexpTrackingTranslationText.forCustomsStatusPleaseSearchBy} <b>{dexpTrackingTranslationText.ccn}</b>.
                  </Alert>
                )}
              </Box>
              {/* Status Section */}
              <Card
                className={classes.statusCard}
                style={{ borderLeftColor: getStatusColor(trackingData) }}
              >
                <Box className={classes.statusHeader}>
                  <Typography
                    className={classes.statusChip}
                    style={{
                      color: getStatusColor(trackingData),
                      '--statusDotColor': getStatusColor(trackingData)
                    }}
                  >
                    {trackingData.status}
                  </Typography>
                </Box>

                <Typography className={classes.containerInfo}>
                  {dexpTrackingTranslationText.cntr}: {trackingData.containerNumber} • {dexpTrackingTranslationText.vessel}: {trackingData.vesselName}
                </Typography>

                <Box className={classes.daysContainer}>
                  <Typography className={classes.daysNumber}>
                    {trackingData.daysAtPort || 0}
                  </Typography>
                  <Typography className={classes.daysLabel}>
                    {dexpTrackingTranslationText.daysAtPort}
                  </Typography>
                </Box>
              </Card>

              {/* Compliance Overview Section */}
              {previousTrackingType === 'CONTAINER' && (
                <Box mb={3}>
                  <Card style ={containsRejected ? {border: '1px solid #fd7e14'} : {border: '1px solid transparent'}}>
                    <Box className={classes.bottomSpace}>
                      <Box className={classes.complianceHeader}>
                        <Typography variant="h6" gutterBottom>
                          {dexpTrackingTranslationText.complianceSummaryTitle}
                        </Typography>
                      </Box>

                      <Box className={classes.complianceContent}>
                        {/* Master Level Section */}
                        <Box className={`${classes.complianceSection} master-level`}>
                          <Typography variant="h6" className={classes.sectionTitle}>
                            {dexpTrackingTranslationText.masterLevelTitle}
                          </Typography>
                          <Typography variant="body2" className={classes.sectionDescription}>
                            {masterLevelStats.total > 1 ? `${masterLevelStats.total} ${dexpTrackingTranslationText.primaryACIManifests}` : dexpTrackingTranslationText.primaryACIOceanManifest}
                          </Typography>
                          <Box className={classes.statusContainer}>
                            {/* Accepted Status */}
                            {masterLevelStats.accepted > 0 && (
                              <Card className={`${classes.csStatusCard} ${classes.acceptedStatus}`}>
                                <span className={classes.statusDot} />
                                <Typography variant="body2" className={classes.statusText}>
                                  {masterLevelStats.accepted} {dexpTrackingTranslationText.acceptedCBSA}
                                </Typography>
                              </Card>
                            )}

                            {/* Submitted Status */}
                            {masterLevelStats.submitted > 0 && (
                              <Card className={`${classes.csStatusCard} ${classes.acceptedStatus}`}>
                                <span className={classes.statusDot} />
                                <Typography variant="body2" className={classes.statusText}>
                                  {masterLevelStats.submitted} {dexpTrackingTranslationText.submitted}
                                </Typography>
                              </Card>
                            )}

                            {/* Rejected Status */}
                            {masterLevelStats.rejected > 0 && (
                              <Card className={`${classes.csStatusCard} ${classes.rejectedStatus}`}>
                                <span className={classes.statusDot} />
                                <Typography variant="body2" className={classes.statusText}>
                                  {masterLevelStats.rejected} {dexpTrackingTranslationText.rejected}
                                </Typography>
                              </Card>
                            )}

                            {/* No Data Available */}
                            {masterLevelStats.total === 0 && (
                              <Card className={`${classes.csStatusCard} ${classes.noDataStatus}`}>
                                <span className={classes.statusDot} />
                                <Typography variant="body2" className={classes.statusText}>
                                  {dexpTrackingTranslationText.dataNotAvailable}
                                </Typography>
                              </Card>
                            )}
                          </Box>
                          
                          {/* Note about search */}
                          <Box style = {{paddingTop: '10px'}}>
                            <Typography variant="caption" className={classes.searchNote}>
                              {containsRejected && dexpTrackingTranslationText.note}
                            </Typography>
                          </Box>
                        </Box>

                        <Divider orientation="vertical" flexItem className={classes.dividerVertical} />

                        {/* House Level Section */}
                        <Box className={`${classes.complianceSection} house-level`}>
                          <Typography variant="h6" className={classes.sectionTitle}>
                            {dexpTrackingTranslationText.houseLevelTitle}
                          </Typography>
                          <Typography variant="body2" className={classes.sectionDescription}>
                            {houseLevelStats.totalEhbls} {dexpTrackingTranslationText.ehbls} {houseLevelStats.totalEhbls > 0 ? 'submitted via CrimsonLogic' : ''}
                          </Typography>
                          <Box className={classes.statusContainer}>
                            {/* Released Status */}
                            {houseLevelStats.released > 0 && (
                              <Card className={`${classes.csStatusCard} ${classes.releasedStatus}`}>
                                <span className={classes.statusDot} />
                                <Typography variant="body2" className={classes.statusText}>
                                  {houseLevelStats.released} {dexpTrackingTranslationText.released}
                                </Typography>
                              </Card>
                            )}

                            {/* Accepted Status */}
                            {houseLevelStats.accepted > 0 && (
                              <Card className={`${classes.csStatusCard} ${classes.acceptedStatus}`}>
                                <span className={classes.statusDot} />
                                <Typography variant="body2" className={classes.statusText}>
                                  {houseLevelStats.accepted} {dexpTrackingTranslationText.accepted}
                                </Typography>
                              </Card>
                            )}

                            {/* Submitted Status */}
                            {houseLevelStats.submitted > 0 && (
                              <Card className={`${classes.csStatusCard} ${classes.acceptedStatus}`}>
                                <span className={classes.statusDot} />
                                <Typography variant="body2" className={classes.statusText}>
                                  {houseLevelStats.submitted} {dexpTrackingTranslationText.submitted}
                                </Typography>
                              </Card>
                            )}

                            {/* Held Status */}
                            {houseLevelStats.held > 0 && (
                              <Card className={`${classes.csStatusCard} ${classes.heldStatus}`}>
                                <span className={classes.statusDot} />
                                <Typography variant="body2" className={classes.statusText}>
                                  {houseLevelStats.held} {dexpTrackingTranslationText.held}
                                </Typography>
                              </Card>
                            )}

                            {/* Rejected Status */}
                            {houseLevelStats.rejected > 0 && (
                              <Card className={`${classes.csStatusCard} ${classes.rejectedStatus}`}>
                                <span className={classes.statusDot} />
                                <Typography variant="body2" className={classes.statusText}>
                                  {houseLevelStats.rejected} {dexpTrackingTranslationText.rejected}
                                </Typography>
                              </Card>
                            )}

                            {/* No Data Available */}
                            {houseLevelStats.totalEhbls === 0 && (
                              <Card className={`${classes.csStatusCard} ${classes.noDataStatus}`}>
                                <span className={classes.statusDot} />
                                <Typography variant="body2" className={classes.statusText}>
                                  {dexpTrackingTranslationText.dataNotAvailable}
                                </Typography>
                              </Card>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Card>
                </Box>
              )}

              {/* Timeline Section */}
              <Card className={classes.timelineCard}>
                <Typography variant="h4"> {dexpTrackingTranslationText.timelineTitle} </Typography>
                <Divider style={{ margin: '16px 0' }} />
                <Timeline className={classes.timelineContainer}>
                  {sortedEvents.map((event, index) => (
                    <TrackingTimelineItem
                      key={event.id}
                      classes={classes}
                      event={event}
                      index={index}
                      total={sortedEvents.length}
                      isExpanded={!!expandedItems[event.id]}
                      onToggle={event.expandable ? () => toggleExpanded(event.id) : undefined}
                    />
                  ))}
                </Timeline>
              </Card>
            </>
          )}
        </>
      )}
        {showLoginRegisterButton && (
          <Card className={classes.searchContainer}>
            <Box className={classes.searchForm}>
              <CngButton
                className={classes.loginButton}
                onClick={handleLogin}
                size="large"
              >
                {dexpTrackingTranslationText.loginButton}
              </CngButton>
              <CngButton
                className={classes.registerButton}
                onClick={handleRegister}
                size="large"
              >
                {dexpTrackingTranslationText.registerButton}
              </CngButton>          
            </Box>
          </Card>
        )}      
      </Box>
  )
}

export default TrackingPage